import * as React from 'react';

import { checkUserReferrerCode, UserReferrerValidationErrorCode } from '../../api/rest/user';
import { logAmplitudeEvent } from '../events/amplitudeEvents';

// Re-export the enum for consumers of this module
export { UserReferrerValidationErrorCode as ReferrerCodeError } from '../../api/rest/user';

/**
 * Represents the state of referrer code validation.
 */
export type ReferrerCodeInfo = {
    isLoading: boolean;
    isReferrerCodeValid?: boolean;
    referrerFirstName?: string;
    specialReferralCodeName?: string;
    error?: UserReferrerValidationErrorCode;
};

/**
 * Regex to validate referrer code format.
 * Matches exactly 6 lowercase alphabetic characters.
 * Note: Input should be lowercased and have whitespace removed before testing.
 */
export const REFERRAL_CODE_REGEX = /^[a-z]{6}$/;

/**
 * Formats a referrer code by converting to lowercase and removing whitespace.
 * @param referrerCode - The raw referrer code input
 * @returns The formatted referrer code
 */
export function formatReferrerCode(referrerCode: string): string {
    return referrerCode.toLowerCase().replace(/\s/g, '');
}

/**
 * Custom hook that validates a referrer code against the backend API.
 *
 * Triggers validation when the referrer code is exactly 6 lowercase letters.
 * The input is sanitized (lowercased, whitespace removed) before validation.
 *
 * @param referrerCode - The raw referrer code input from the user
 * @returns ReferrerCodeInfo object with validation state, or undefined if code is too short/invalid format
 *   - `isLoading`: Whether the API call is in progress
 *   - `isReferrerCodeValid`: Whether the code passed all backend validations
 *   - `referrerFirstName`: The referrer's first name (shown on success)
 *   - `specialReferralCodeName`: Name for promotional codes
 *   - `error`: Error code from backend ('different_region', 'referee_limit_reached', etc.)
 */
export function useReferrerCodeInfo(referrerCode: string): ReferrerCodeInfo | undefined {
    const [referrerCodeInfo, setReferrerCodeInfo] = React.useState<ReferrerCodeInfo | undefined>(undefined);
    React.useEffect(() => {
        // Race condition guard: If the user types quickly (e.g., "abcdef" -> "abcdefg"),
        // multiple API calls may be in flight simultaneously. Without this check, a slower
        // response for "abcdef" could overwrite the correct result for "abcdefg".
        // The cleanup function sets isCancelled=true when the effect re-runs, ensuring
        // stale responses are ignored.
        let isCancelled = false;
        const formattedReferrerCode = formatReferrerCode(referrerCode);
        if (REFERRAL_CODE_REGEX.test(formattedReferrerCode)) {
            setReferrerCodeInfo({ isLoading: true });
            checkUserReferrerCode(formattedReferrerCode)
                .then(({ isReferrerCodeValid, referrerFirstName, specialReferralCodeName, error }) => {
                    if (!isCancelled) {
                        // Only update state if this effect is still current
                        setReferrerCodeInfo({
                            isLoading: false,
                            isReferrerCodeValid,
                            referrerFirstName,
                            specialReferralCodeName,
                            error,
                        });
                        if (isReferrerCodeValid) {
                            logAmplitudeEvent({ name: 'Registration - Referrer Code Validation Succeeded' });
                        } else {
                            logAmplitudeEvent({ name: 'Registration - Referrer Code Validation Failed' });
                        }
                    }
                })
                .catch((err) => {
                    console.error(`Error checking user referrer code: ${err}`);
                    if (!isCancelled) {
                        setReferrerCodeInfo({
                            isLoading: false,
                            isReferrerCodeValid: false,
                            error: UserReferrerValidationErrorCode.Unknown,
                        });
                    }
                });
            return () => {
                isCancelled = true;
            };
        } else {
            setReferrerCodeInfo(undefined);
        }
    }, [referrerCode]);
    return referrerCodeInfo;
}

/**
 * Checks whether an error message should be displayed for a referrer code.
 * Shows error when:
 * - Code is not exactly 6 lowercase letters
 * - Code is valid format but failed backend validation
 *
 * @param formattedReferrerCode - The formatted (lowercased, whitespace removed) referrer code
 * @param referrerCodeInfo - The validation info from useReferrerCodeInfo
 * @returns Whether to display an error message
 */
export function checkShouldDisplayErrorMessage(
    formattedReferrerCode: string,
    referrerCodeInfo: ReferrerCodeInfo | undefined
): boolean {
    const isValidFormat = REFERRAL_CODE_REGEX.test(formattedReferrerCode);
    return (
        (formattedReferrerCode.length >= 6 && !isValidFormat) ||
        (isValidFormat && referrerCodeInfo?.isReferrerCodeValid === false)
    );
}
